// RTCSessionManager — Tsuyaku (translation) WebRTC session lifecycle.
//
// spec §2.3.1:
//   1. POST /api/session/translate → receive ephemeral "ek_..." secret.
//   2. Capture user's mic via getUserMedia (single audio track).
//   3. Create RTCPeerConnection, addTrack(mic), createDataChannel("oai-events").
//   4. Exchange SDP with https://api.openai.com/v1/realtime?model=...
//      using the ephemeral secret as Bearer.
//   5. Remote translated audio arrives via ontrack → play it from an
//      HTMLAudioElement.srcObject. The output_audio.delta event does NOT
//      exist on this transport.
//   6. PTT: track.enabled = true on press, false on release.
//   7. Direction change: DataChannel "session.update" → audio.output.language.

import type {
  TranslateSessionResponse,
  TsuyakuOutputLanguage,
} from "../../lib/api-types";
import type { RealtimeServerEvent } from "./events";

const REALTIME_BASE = "https://api.openai.com/v1/realtime";
// Translation sessions have their own SDP exchange path, distinct from the
// generic /v1/realtime/calls used by "normal" Realtime (future Zasso / Okoshi).
// Using /calls here is rejected with:
//   "Translation sessions are only supported on /v1/realtime/translations."
//
// Endpoint pairing:
//   translation  → mint: /v1/realtime/translations/client_secrets
//                  SDP : /v1/realtime/translations
//   realtime (generic, GA) → mint: /v1/realtime/client_secrets
//                            SDP : /v1/realtime/calls
//
// The GA vs. beta constraint still holds: GA ephemeral tokens must exchange
// SDP at a GA endpoint; the older /v1/realtime?model=... is beta-only.
const REALTIME_SDP_URL = `${REALTIME_BASE}/translations`;

export type TsuyakuState =
  | "idle"
  | "connecting"
  | "ready"
  | "holding"
  | "playing"
  | "error"
  | "closed";

export interface TsuyakuSessionCallbacks {
  onState: (state: TsuyakuState) => void;
  onRemoteTrack: (stream: MediaStream) => void;
  onEvent: (event: RealtimeServerEvent) => void;
  onError: (message: string) => void;
}

export class RTCSessionManager {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private localStream: MediaStream | null = null;
  private micTrack: MediaStreamTrack | null = null;
  private state: TsuyakuState = "idle";
  private closed = false;

  constructor(private readonly cb: TsuyakuSessionCallbacks) {}

  getState(): TsuyakuState {
    return this.state;
  }

  getMicTrack(): MediaStreamTrack | null {
    return this.micTrack;
  }

  // ── Public lifecycle ───────────────────────────────────────
  async connect(
    outputLanguage: TsuyakuOutputLanguage,
    audioInputId?: string | null,
  ): Promise<void> {
    if (this.closed) throw new Error("manager is closed");
    if (this.pc) throw new Error("already connected");
    this.setState("connecting");

    // ── 1. Mint ephemeral secret via our Worker ──
    const tokenRes = await fetch("/api/session/translate", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ output_language: outputLanguage }),
    });
    if (!tokenRes.ok) {
      const text = await tokenRes.text().catch(() => "");
      throw new Error(
        `mint ephemeral token failed: ${tokenRes.status} ${text.slice(0, 200)}`,
      );
    }
    const session = (await tokenRes.json()) as TranslateSessionResponse;
    if (session.endpointVariant === "client_secrets") {
      console.warn(
        "[tsuyaku] backend used fallback client_secrets endpoint",
      );
    }

    // ── 2. Capture microphone ──
    const audioConstraints: MediaTrackConstraints = audioInputId
      ? { deviceId: { exact: audioInputId } }
      : {};
    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        ...audioConstraints,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    });
    const track = this.localStream.getAudioTracks()[0];
    if (!track) throw new Error("no audio track from getUserMedia");
    this.micTrack = track;
    // PTT starts in released state → track is muted.
    track.enabled = false;

    // ── 3. RTCPeerConnection + DataChannel + tracks ──
    this.pc = new RTCPeerConnection();

    this.pc.addEventListener("connectionstatechange", () => {
      const s = this.pc?.connectionState;
      if (s === "failed" || s === "disconnected" || s === "closed") {
        this.setState("error");
        this.cb.onError(`peer connection ${s}`);
      }
    });

    this.pc.addEventListener("track", (ev) => {
      const [stream] = ev.streams;
      if (stream) {
        this.cb.onRemoteTrack(stream);
      }
    });

    this.pc.addTrack(track, this.localStream);

    this.dc = this.pc.createDataChannel("oai-events");
    this.dc.addEventListener("open", () => {
      this.setState("ready");
    });
    this.dc.addEventListener("message", (ev) => {
      this.handleDataChannelMessage(ev.data);
    });
    this.dc.addEventListener("error", (ev) => {
      console.error("[tsuyaku] datachannel error", ev);
    });

    // ── 4. SDP exchange ──
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);

    const sdpRes = await fetch(REALTIME_SDP_URL, {
      method: "POST",
      headers: {
        authorization: `Bearer ${session.clientSecret}`,
        "content-type": "application/sdp",
      },
      body: offer.sdp ?? "",
    });
    if (!sdpRes.ok) {
      const text = await sdpRes.text().catch(() => "");
      throw new Error(
        `SDP exchange failed: ${sdpRes.status} ${text.slice(0, 200)}`,
      );
    }
    const answerSdp = await sdpRes.text();
    await this.pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
  }

  /** Update session.audio.output.language via DataChannel. */
  setOutputLanguage(lang: TsuyakuOutputLanguage): void {
    if (!this.dc || this.dc.readyState !== "open") return;
    const evt = {
      type: "session.update",
      session: { audio: { output: { language: lang } } },
    };
    this.dc.send(JSON.stringify(evt));
  }

  /** PTT press — unmute mic track. */
  startTransmit(): void {
    if (!this.micTrack) return;
    this.micTrack.enabled = true;
    if (this.state === "ready" || this.state === "playing") {
      this.setState("holding");
    }
  }

  /** PTT release — mute mic track. Model auto-translates continuous stream. */
  stopTransmit(): void {
    if (!this.micTrack) return;
    this.micTrack.enabled = false;
    if (this.state === "holding") {
      this.setState("ready");
    }
  }

  close(): void {
    this.closed = true;
    try {
      this.dc?.close();
    } catch {
      /* ignore */
    }
    this.dc = null;
    try {
      this.pc?.close();
    } catch {
      /* ignore */
    }
    this.pc = null;
    this.micTrack?.stop();
    this.localStream?.getTracks().forEach((t) => t.stop());
    this.localStream = null;
    this.micTrack = null;
    if (this.state !== "error") this.setState("closed");
  }

  // ── Internals ──────────────────────────────────────────────
  private setState(next: TsuyakuState) {
    if (this.state === next) return;
    this.state = next;
    this.cb.onState(next);
  }

  private handleDataChannelMessage(raw: unknown) {
    if (typeof raw !== "string") return;
    let parsed: RealtimeServerEvent;
    try {
      parsed = JSON.parse(raw) as RealtimeServerEvent;
    } catch (err) {
      console.warn("[tsuyaku] non-JSON dc message", err);
      return;
    }
    // Upgrade `ready` → `playing` when model starts emitting a response.
    if (parsed.type === "response.output_audio_transcript.delta") {
      if (this.state === "ready" || this.state === "holding") {
        this.setState("playing");
      }
    }
    if (parsed.type === "response.done") {
      if (this.state === "playing") this.setState("ready");
    }
    this.cb.onEvent(parsed);
  }
}
